/**
 * Postman Collection Parser
 * Supports Postman Collection Format v2.0 and v2.1
 */

/**
 * Parse a Postman collection JSON and extract all requests
 * @param {Object} collection - Postman collection JSON object
 * @returns {Object} - { requests: [], collectionName: string, version: string }
 */
export const parsePostmanCollection = (collection) => {
  try {
    // Validate basic structure
    if (!collection || typeof collection !== 'object') {
      throw new Error('Invalid collection: must be a valid JSON object');
    }

    // Extract collection info
    const collectionName = collection.info?.name || 'Untitled Collection';
    const schema = collection.info?.schema || '';
    const version = schema.includes('v2.1') ? 'v2.1' : schema.includes('v2.0') ? 'v2.0' : 'unknown';

    // Extract all items (requests) from the collection
    const items = collection.item || [];
    if (items.length === 0) {
      throw new Error('No requests found in collection');
    }

    // Flatten nested folders and extract all requests
    const requests = flattenItems(items, []);

    if (requests.length === 0) {
      throw new Error('No valid requests found in collection');
    }

    return {
      requests,
      collectionName,
      version,
    };
  } catch (error) {
    throw new Error(`Failed to parse collection: ${error.message}`);
  }
};

/**
 * Recursively flatten nested item groups (folders) to extract all requests
 * @param {Array} items - Array of items from collection
 * @param {Array} path - Current folder path for organization
 * @returns {Array} - Flattened array of request objects
 */
const flattenItems = (items, path = []) => {
  const requests = [];

  items.forEach((item) => {
    if (item.request) {
      // This is a request item
      const requestDetails = extractRequestDetails(item, path);
      if (requestDetails) {
        requests.push(requestDetails);
      }
    } else if (item.item && Array.isArray(item.item)) {
      // This is a folder (item group), recurse into it
      const folderName = item.name || 'Folder';
      const newPath = [...path, folderName];
      const nestedRequests = flattenItems(item.item, newPath);
      requests.push(...nestedRequests);
    }
  });

  return requests;
};

/**
 * Extract details from a single request item
 * @param {Object} item - Postman request item
 * @param {Array} path - Folder path
 * @returns {Object|null} - Normalized request object or null if invalid
 */
export const extractRequestDetails = (item, path = []) => {
  try {
    const request = item.request;
    
    // Handle string request (simple GET)
    if (typeof request === 'string') {
      return {
        id: generateId(),
        name: item.name || 'Untitled Request',
        folder: path.join(' > '),
        url: request,
        method: 'GET',
        headers: {},
        body: '',
        auth: null,
      };
    }

    // Validate request object
    if (!request || typeof request !== 'object') {
      console.warn('Invalid request format:', item.name);
      return null;
    }

    // Extract URL
    const url = parsePostmanUrl(request.url);
    if (!url) {
      console.warn('Request has no URL:', item.name);
      return null;
    }

    // Extract method (default to GET)
    const method = (request.method || 'GET').toUpperCase();

    // Extract headers
    const headers = parsePostmanHeaders(request.header || []);

    // Extract body
    const body = parsePostmanBody(request.body);

    // Extract authentication
    const auth = parsePostmanAuth(request.auth || item.auth);

    return {
      id: generateId(),
      name: item.name || 'Untitled Request',
      folder: path.join(' > '),
      url,
      method,
      headers,
      body,
      auth,
    };
  } catch (error) {
    console.error('Error extracting request details:', error);
    return null;
  }
};

/**
 * Parse Postman URL format (can be string or object)
 * @param {String|Object} url - Postman URL
 * @returns {String|null} - Full URL string
 */
export const parsePostmanUrl = (url) => {
  if (!url) return null;

  // If URL is a string, return it directly
  if (typeof url === 'string') {
    return url;
  }

  // If URL is an object, construct from parts
  if (typeof url === 'object') {
    // Try to use the 'raw' field if available
    if (url.raw) {
      return url.raw;
    }

    // Otherwise, construct from protocol, host, and path
    try {
      const protocol = url.protocol || 'https';
      const host = Array.isArray(url.host) ? url.host.join('.') : url.host || '';
      const path = Array.isArray(url.path) ? '/' + url.path.join('/') : url.path || '';
      const query = url.query && Array.isArray(url.query) && url.query.length > 0
        ? '?' + url.query
            .filter(q => q.key)
            .map(q => `${encodeURIComponent(q.key)}=${encodeURIComponent(q.value || '')}`)
            .join('&')
        : '';

      return `${protocol}://${host}${path}${query}`;
    } catch (error) {
      console.error('Error constructing URL from object:', error);
      return null;
    }
  }

  return null;
};

/**
 * Parse Postman headers array to key-value object
 * @param {Array} headers - Array of header objects
 * @returns {Object} - Headers object
 */
export const parsePostmanHeaders = (headers) => {
  if (!Array.isArray(headers)) {
    return {};
  }

  const headerObj = {};
  headers.forEach((header) => {
    // Only include enabled headers
    if (header.disabled !== true && header.key) {
      headerObj[header.key] = header.value || '';
    }
  });

  return headerObj;
};

/**
 * Parse Postman request body
 * @param {Object} body - Postman body object
 * @returns {String} - Body content as string
 */
export const parsePostmanBody = (body) => {
  if (!body || typeof body !== 'object') {
    return '';
  }

  const mode = body.mode;

  switch (mode) {
    case 'raw':
      return body.raw || '';

    case 'urlencoded':
      if (Array.isArray(body.urlencoded)) {
        return body.urlencoded
          .filter(item => item.disabled !== true && item.key)
          .map(item => `${encodeURIComponent(item.key)}=${encodeURIComponent(item.value || '')}`)
          .join('&');
      }
      return '';

    case 'formdata':
      if (Array.isArray(body.formdata)) {
        // For form data, we'll convert to a comment format since the app expects raw body
        const formFields = body.formdata
          .filter(item => item.disabled !== true && item.key)
          .map(item => `${item.key}: ${item.value || ''}`)
          .join('\n');
        return `# Form Data\n${formFields}`;
      }
      return '';

    case 'file':
      return '# File upload (not supported - please configure manually)';

    case 'graphql':
      if (body.graphql) {
        const query = body.graphql.query || '';
        const variables = body.graphql.variables ? `\n\nVariables:\n${body.graphql.variables}` : '';
        return query + variables;
      }
      return '';

    default:
      return '';
  }
};

/**
 * Parse Postman authentication and map to application auth types
 * @param {Object} auth - Postman auth object
 * @returns {Object|null} - Auth configuration or null
 */
export const parsePostmanAuth = (auth) => {
  if (!auth || typeof auth !== 'object' || auth.type === 'noauth') {
    return null;
  }

  const authType = auth.type;

  switch (authType) {
    case 'bearer':
      // Extract bearer token
      const bearerArray = auth.bearer || [];
      const tokenObj = bearerArray.find(item => item.key === 'token');
      const token = tokenObj?.value || '';
      
      return {
        type: 'bearer',
        token,
        _original: 'bearer',
      };

    case 'apikey':
      // Map API key to custom header
      const apikeyArray = auth.apikey || [];
      const keyObj = apikeyArray.find(item => item.key === 'key');
      const valueObj = apikeyArray.find(item => item.key === 'value');
      const inObj = apikeyArray.find(item => item.key === 'in');
      
      const key = keyObj?.value || 'X-API-Key';
      const value = valueObj?.value || '';
      const inLocation = inObj?.value || 'header';
      
      if (inLocation === 'header') {
        return {
          type: 'header',
          key,
          value,
          _original: 'apikey',
        };
      } else {
        // Query parameter API keys not supported
        return {
          type: 'unsupported',
          message: `API Key in ${inLocation} not supported. Please add manually.`,
          _original: 'apikey',
        };
      }

    case 'basic':
      return {
        type: 'unsupported',
        message: 'Basic Auth not supported. Consider using Bearer Token or Custom Header.',
        _original: 'basic',
      };

    case 'oauth1':
    case 'oauth2':
      return {
        type: 'unsupported',
        message: 'OAuth not supported. Please configure authentication manually.',
        _original: authType,
      };

    case 'digest':
    case 'hawk':
    case 'awsv4':
    case 'ntlm':
      return {
        type: 'unsupported',
        message: `${authType} auth not supported. Please configure authentication manually.`,
        _original: authType,
      };

    default:
      return {
        type: 'unsupported',
        message: 'Unknown auth type. Please configure authentication manually.',
        _original: authType,
      };
  }
};

/**
 * Generate a simple unique ID for requests
 * @returns {String} - Unique ID
 */
const generateId = () => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
